import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Check, Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { id: "crisis", label: "I need support" },
  { id: "general", label: "General question" },
  { id: "partner", label: "Partnership" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "crisis", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("We've got your message.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="pt-12 pb-4" data-testid="contact-emergency-banner">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="rounded-2xl bg-navy text-white p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-teal flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-display text-xl mb-1">In immediate danger?</p>
              <p className="text-white/80 text-sm">Call or text <a href="tel:988" className="underline font-bold text-teal">988</a> — free, 24/7. For emergencies, dial <a href="tel:911" className="underline font-bold text-teal">911</a>.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20" data-testid="contact-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Get in touch</p>
          <h1 className="font-display text-5xl sm:text-6xl text-navy leading-[1.05]">
            We're ready to <span className="hl-teal">listen.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-ink leading-relaxed">
            Share as much or as little as you'd like. A member of our team will respond — usually within minutes.
          </p>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="contact-form-section">
        <div className="max-w-2xl mx-auto px-6 lg:px-10">
          {done ? (
            <div className="gh-card !p-10 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-teal flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-3xl text-navy mb-3">We hear you.</h3>
              <p className="text-muted-ink mb-6">
                Your message is with our team. If this is urgent, please also call or text <a href="tel:988" className="text-teal font-bold underline">988</a> right now.
              </p>
              <button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", subject: "crisis", message: "" }); }} className="btn-outline" data-testid="contact-send-another">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="gh-card !p-8 lg:!p-10 space-y-5" data-testid="contact-form">
              <div>
                <label className="eyebrow mb-3 block">What's this about?</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {SUBJECTS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, subject: s.id }))}
                      className={`py-3 rounded-full text-sm font-bold border-2 transition ${
                        form.subject === s.id
                          ? "border-teal bg-teal text-white"
                          : "border-slate-200 text-navy hover:border-teal/40"
                      }`}
                      data-testid={`contact-subject-${s.id}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <input required placeholder="Your name" value={form.name} onChange={update("name")} className="gh-input" data-testid="contact-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={update("email")} className="gh-input" data-testid="contact-email" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} className="gh-input" data-testid="contact-phone" />
              <textarea required rows={5} placeholder="Share what's on your mind..." value={form.message} onChange={update("message")} className="gh-input resize-none" data-testid="contact-message" />
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="contact-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="py-20 bg-cream" data-testid="contact-channels-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {[
              { icon: <Phone className="w-6 h-6" />, title: "Call 988", desc: "Free, confidential crisis line — available 24/7.", href: "tel:988" },
              { icon: <MessageCircle className="w-6 h-6" />, title: "Text HOME to 741741", desc: "Crisis Text Line. Available any hour.", href: "sms:741741" },
              { icon: <Mail className="w-6 h-6" />, title: "care@generationalh.com", desc: "Non-urgent questions — we reply within a day.", href: "mailto:care@generationalh.com" },
            ].map((c, i) => (
              <a key={i} href={c.href} className="gh-card block" data-testid={`contact-channel-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-light flex items-center justify-center text-teal mb-5">
                  {c.icon}
                </div>
                <h3 className="font-display text-xl mb-2 text-navy">{c.title}</h3>
                <p className="text-muted-ink text-[15px]">{c.desc}</p>
              </a>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <a href="tel:7049999056" className="gh-card block" data-testid="contact-phone-card">
              <div className="w-11 h-11 rounded-xl bg-teal-light flex items-center justify-center text-teal mb-5">
                <Phone className="w-6 h-6" />
              </div>
              <p className="eyebrow mb-2">Office</p>
              <h3 className="font-display text-2xl mb-1 text-navy">704-999-9056</h3>
              <p className="text-muted-ink text-[15px]">Monday–Friday, 9am–5pm ET. Non-clinical inquiries.</p>
            </a>
            <a href="https://maps.google.com/?q=719+East+Blvd+Charlotte+NC+28203" target="_blank" rel="noopener noreferrer" className="gh-card block" data-testid="contact-address-card">
              <div className="w-11 h-11 rounded-xl bg-teal-light flex items-center justify-center text-teal mb-5">
                <MapPin className="w-6 h-6" />
              </div>
              <p className="eyebrow mb-2">Headquarters</p>
              <h3 className="font-display text-2xl mb-1 text-navy">719 East Blvd</h3>
              <p className="text-muted-ink text-[15px]">Charlotte, NC 28203</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
